import logging from "utils/logging";
import { createSlice, createSelector } from "@reduxjs/toolkit";
import Provider from "provider";

const log = logging.getLogger("Auth Slice"); //eslint-disable-line no-unused-vars

const authSlice = createSlice({
  name: "auth",
  initialState: {
    user: null,
    loading: true,
    error: null,
    authUnavailable: false,
    destination: null,
    modalOpen: false,
    modalMode: "login", // "login" or "signup"
  },
  reducers: {
    userLoaded: (state, action) => {
      log.debug("Authed user loaded. Action: ", action);
      state.user = action.payload.user;
      state.loading = false;
      state.error = null;
      state.authUnavailable = false;
    },
    authProviderUnavailable: (state) => {
      // The auth provider (Clerk) couldn't initialize — render unauthenticated,
      // but flag it so auth-gated surfaces can show an error instead of failing
      // silently.
      state.user = null;
      state.loading = false;
      state.authUnavailable = true;
    },
    stopWatching: (state) => {
      state.loading = false;
    },
    loggingOut: (state) => {
      state.loading = true;
      state.user = null;
      state.destination = null;
    },
    loggedOut: (state) => {
      state.user = null;
      state.loading = false;
      state.error = null;
    },
    error: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },
    redirectToAuthFrom(state, action) {
      state.destination = action.payload.destination;
    },
    showSignupModal: (state) => {
      state.modalOpen = true;
      state.modalMode = "signup";
    },
    showLoginModal: (state) => {
      state.modalOpen = true;
      state.modalMode = "login";
    },
    closeAuthModal: (state) => {
      state.modalOpen = false;
    },
    switchAuthMode: (state) => {
      state.modalMode = state.modalMode === "login" ? "signup" : "login";
    },
  },
});

export const { userLoaded, authProviderUnavailable, stopWatching, loggingOut, loggedOut, error, redirectToAuthFrom, showSignupModal, showLoginModal, closeAuthModal, switchAuthMode } = authSlice.actions;

export const watchAuthedUserAction = (user) => ({
  type: 'auth/watchAuthedUser',
  payload: { user }
});

export const stopWatchingAuthedUserAction = () => ({
  type: 'auth/stopWatchingAuthedUser'
});

export const logOutAction = () => async (dispatch) => {
  dispatch(loggingOut());
  try {
    await Provider.logOut();
    dispatch(loggedOut());
  } catch (err) {
    dispatch(error(err.message));
  }
};

/* ---- Selectors ---- */

const selectAuthState = (state) => state.auth;

const selectAuthModalState = createSelector(selectAuthState, (s) => 
  {
    return {
      modalOpen: s.modalOpen,
      modalMode: s.modalMode
    };
  }
);

const selectCurrentUser = createSelector(selectAuthState, (s) => s.user);
const selectCurrentUserId = createSelector(selectAuthState, (s) =>
  s.user ? s.user.uid : null
);
const selectAuthLoading = createSelector(
  selectAuthState,
  (s) => s.loading
);
const selectUserIsAuthed = createSelector(selectAuthState, (s) => {
  const user = s.user;
  return user && user.uid && !user.isAnonymous;
});
const selectDestination = createSelector(selectAuthState, (s) => s.destination);
const selectAuthUnavailable = createSelector(selectAuthState, (s) => s.authUnavailable);

export {
  selectCurrentUser,
  selectCurrentUserId,
  selectAuthModalState,
  selectAuthLoading,
  selectUserIsAuthed,
  selectDestination,
  selectAuthUnavailable,
};

export default authSlice.reducer;
